import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { getRequestSummaryForDialog } from './adjustmentsShared';

const ACTION_CONFIG = {
  approve: {
    title: 'Approve Adjustment',
    confirmLabel: 'Approve Request',
    color: 'success',
    description: 'This moves the request out of approval review and into the next backend state.',
    allowsNote: true,
  },
  reject: {
    title: 'Reject Adjustment',
    confirmLabel: 'Reject Request',
    color: 'error',
    description: 'This permanently rejects the adjustment request. Use the note field to capture the reason for the decision.',
    allowsNote: true,
  },
  cancel: {
    title: 'Cancel Adjustment',
    confirmLabel: 'Cancel Request',
    color: 'error',
    description: 'This cancels an awaiting-execution request before the execution step is run.',
    allowsNote: false,
  },
  execute: {
    title: 'Execute Adjustment',
    confirmLabel: 'Execute Request',
    color: 'primary',
    description: 'This runs the backend execution step for the approved adjustment request.',
    allowsNote: false,
  },
};

export default function AdjustmentActionDialog({
  action,
  open,
  request,
  note,
  submitting,
  error,
  onClose,
  onNoteChange,
  onSubmit,
}) {
  const config = action ? ACTION_CONFIG[action] : null;

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{config.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {config.description}
          </Typography>

          {request ? (
            <Alert severity="info" variant="outlined">
              {getRequestSummaryForDialog(request)}
            </Alert>
          ) : null}

          {config.allowsNote ? (
            <TextField
              label="Decision note"
              value={note}
              onChange={(event) => onNoteChange(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              helperText="Optional, but recommended for audit clarity."
            />
          ) : null}

          {error ? (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={submitting}>
          Close
        </Button>
        <LoadingButton variant="contained" color={config.color} onClick={onSubmit} loading={submitting}>
          {config.confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

AdjustmentActionDialog.propTypes = {
  action: PropTypes.oneOf(['approve', 'reject', 'cancel', 'execute']),
  error: PropTypes.string,
  note: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onNoteChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  request: PropTypes.shape({
    adjustment_type: PropTypes.string,
    amount: PropTypes.string,
    asset_code: PropTypes.string,
    direction: PropTypes.string,
    reason_code: PropTypes.string,
    request_id: PropTypes.string,
  }),
  submitting: PropTypes.bool.isRequired,
};
